/* eslint-disable max-lines */
// Helpers for trip durations that may exceed 24h (multi-day trips).
//
// A trip carries its whole length in `durationMinutes` (int, minutes). The
// "HH:mm" departure/arrival strings can only show a time of day, so a length
// above 24h is inexpressible there and the arrival is kept in sync as
// (departure + length) % 24h.

// 96:00 — the longest trip the form accepts.
export const MAX_TRIP_DURATION_MINUTES = 5760;

// "HH:mm" or "HH:mm:ss" -> minutes since midnight. NaN when malformed.
export const timeStringToMinutes = (time: string): number => {
  if (!time) return NaN;
  const [hours, minutes] = time.split(":");
  return Number(hours) * 60 + Number(minutes);
};

// Total minutes -> "HH:mm" on a 24h clock (wraps past midnight).
export const minutesToTimeString = (totalMinutes: number): string => {
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

// User-typed length "H:MM".."HHH:MM" (hours may exceed 23, e.g. "31:30") ->
// total minutes, or null while the text isn't a complete hours:minutes value.
// Range (1..MAX_TRIP_DURATION_MINUTES) is enforced by the yup schema so the
// user gets a message instead of a dead input.
export const parseDurationInput = (text: string): number | null => {
  const match = /^(\d{1,3}):([0-5]?\d)$/.exec(text.trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

// Total minutes -> "HH:MM" length text ("08:30", "31:30").
export const formatDurationInput = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

// Calendar days between a departure clock and an instant `minutesAfter` it: 0 when the
// arrival is the same day, 1 the next day, and so on. 22:00 plus 4h is 1; 22:00 plus 28h
// is 2; 10:00 plus 28h is 1. This is the "+N ditë" marker every screen prints beside a clock.
export const calendarDaysAfter = (
  departureTime: string | undefined,
  minutesAfter: number | undefined
): number => {
  if (!departureTime || typeof minutesAfter !== "number" || !Number.isFinite(minutesAfter)) return 0;
  const departure = timeStringToMinutes(departureTime);
  if (isNaN(departure) || minutesAfter < 0) return 0;
  return Math.floor((departure + minutesAfter) / 1440);
};

// "03:30", or "03:30 (+1 ditë)" when the clock falls on a later day.
export const withDayMarker = (clock: string | undefined, days: number): string =>
  days > 0 ? `${clock ?? ""} (+${days} ditë)` : clock ?? "";

// Read-only arrival preview: "15:30", or "02:00 (+1 ditë)" when the arrival lands on a
// later day. Empty string while departure/length are incomplete.
export const formatArrivalPreview = (
  departureTime: string | undefined,
  durationMinutes: number | undefined
): string => {
  if (
    !departureTime ||
    typeof durationMinutes !== "number" ||
    isNaN(durationMinutes) ||
    durationMinutes < 1
  ) {
    return "";
  }
  const departure = timeStringToMinutes(departureTime);
  if (isNaN(departure)) return "";
  const arrival = minutesToTimeString(departure + durationMinutes);
  return withDayMarker(arrival, calendarDaysAfter(departureTime, durationMinutes));
};

// Whole days a trip LASTS: 0 for anything under 24h, 1 for 24h..47h59, 2 for 48h.. Decides
// only whether a length is spelled out beside a clock; the day marker is calendarDaysAfter.
export const wholeTripDays = (durationMinutes: number | undefined): number =>
  typeof durationMinutes === "number" && Number.isFinite(durationMinutes) && durationMinutes > 0
    ? Math.floor(durationMinutes / 1440)
    : 0;

// A length in words, days first, zero parts left out: "1d 6h 10m", "9h 10m", "2d", "45m".
// The marker shown beside a trip in the lists and the bulk screen, so a 30-hour trip reads
// as 30 hours and not as its wrapped clock pair. Empty for 0 or a missing value.
export const formatTripLength = (durationMinutes: number | undefined): string => {
  if (typeof durationMinutes !== "number" || !Number.isFinite(durationMinutes) || durationMinutes <= 0) {
    return "";
  }
  const days = Math.floor(durationMinutes / 1440);
  const hours = Math.floor((durationMinutes % 1440) / 60);
  const minutes = durationMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(" ");
};

// An arrival clock as the screens print it: the day it lands on when that is a later day, and
// the whole length for a trip of a day or more: "02:00 (+1 ditë)", "16:00 (+1 ditë) (1d 6h)".
export const withLengthMarker = (
  departureTime: string | undefined,
  clock: string | undefined,
  durationMinutes: number | undefined
): string => {
  const text = withDayMarker(clock, calendarDaysAfter(departureTime, durationMinutes));
  return wholeTripDays(durationMinutes) > 0 ? `${text} (${formatTripLength(durationMinutes)})` : text;
};

// The trips in a list that share a departure/arrival clock pair with another trip in the
// same list. Only those labels need the trip id to stay tellable apart.
export const collidingTripIds = (
  trips: { id: number; departureTimeString?: string; arrivalTimeString?: string }[]
): Set<number> => {
  const idsByClockPair = new Map<string, number[]>();
  trips.forEach((trip) => {
    const key = `${trip.departureTimeString ?? ""}|${trip.arrivalTimeString ?? ""}`;
    const ids = idsByClockPair.get(key);
    if (ids) ids.push(trip.id);
    else idsByClockPair.set(key, [trip.id]);
  });
  const colliding = new Set<number>();
  idsByClockPair.forEach((ids) => {
    if (ids.length > 1) ids.forEach((id) => colliding.add(id));
  });
  return colliding;
};

// The label a trip picker shows: "08:00 - 12:00", plus " (+N ditë)" when the trip lasts a
// whole day or more, plus " #<id>" when `showId` is set. Ask for the id only on a trip that
// shares its clock pair with another trip in the same list (see collidingTripIds) - those
// two rows are otherwise identical to read.
export const tripTimesLabel = (
  departureTimeString: string | undefined,
  arrivalTimeString: string | undefined,
  durationMinutes: number,
  tripId?: number,
  showId?: boolean
): string => {
  // The arrival carries the day it lands on; a trip of a day or more adds its whole length.
  const arrival = withLengthMarker(departureTimeString, arrivalTimeString, durationMinutes);
  const id = showId && typeof tripId === "number" && tripId > 0 ? ` #${tripId}` : "";
  return `${departureTimeString ?? ""} - ${arrival}${id}`;
};
